import React, { useId } from 'react'

import clsx from 'clsx'
import './ApplyV2InputCheckbox.scss'

interface ApplyV2InputCheckboxProps {
    value?: boolean | null
    isRequired?: boolean
    label?: string
    className?: string
    id?: string | number
    name?: string
    isDisabled?: boolean
    errorMessage?: string | null | boolean
    isShowErrorMessage?: boolean
    onChange?: (value: boolean) => void
    onFocus?: React.FocusEventHandler<HTMLInputElement>
    onBlur?: React.FocusEventHandler<HTMLInputElement>
    isLoading?: boolean
}

const baseClass = 'ApplyV2InputCheckbox'

function ApplyV2InputCheckbox({
    value = false,
    isRequired = false,
    label,
    className = '',
    id,
    name,
    isDisabled = false,
    errorMessage,
    isShowErrorMessage = true,
    onChange = () => {},
    onFocus = () => {},
    onBlur = () => {},
    isLoading = false,
}: ApplyV2InputCheckboxProps) {
    const defaultId = useId()
    const selectId = id ? id.toString() : defaultId

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        onChange(e.target.checked)
    }

    return (
        <div
            className={clsx(baseClass, className, {
                [`${baseClass}--disabled`]: isDisabled,
                [`${baseClass}--error`]: errorMessage,
                [`${baseClass}--loading`]: isLoading,
                [`${className}--disabled`]: className && isDisabled,
                [`${className}--error`]: className && errorMessage,
                [`${className}--loading`]: className && isLoading,
            })}
        >
            <label className={`${baseClass}__wrapper`}>
                <input
                    type="checkbox"
                    id={selectId}
                    name={name ?? selectId}
                    checked={!!value}
                    onChange={handleChange}
                    onBlur={onBlur}
                    onFocus={onFocus}
                    disabled={isDisabled}
                    className={`${baseClass}__input`}
                />
                <span
                    className={clsx(`${baseClass}__checkbox`, {
                        [`${baseClass}__checkbox--checked`]: value,
                        [`${baseClass}__checkbox--disabled`]: isDisabled,
                    })}
                />
                {label && (
                    <span className={`${baseClass}__label`}>
                        {label}
                        {isRequired && '*'}
                    </span>
                )}
            </label>

            {errorMessage && isShowErrorMessage && (
                <div
                    className={clsx(`${baseClass}__error`, {
                        [`${className}__error`]: className,
                    })}
                >
                    <div className={`${baseClass}__error--icon`}>
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path
                                fillRule="evenodd"
                                clipRule="evenodd"
                                d="M8.00006 1.9334C4.64954 1.9334 1.9334 4.64954 1.9334 8.00006C1.9334 11.3506 4.64954 14.0667 8.00006 14.0667C11.3506 14.0667 14.0667 11.3506 14.0667 8.00006C14.0667 4.64954 11.3506 1.9334 8.00006 1.9334ZM0.733398 8.00006C0.733398 3.9868 3.9868 0.733398 8.00006 0.733398C12.0133 0.733398 15.2667 3.9868 15.2667 8.00006C15.2667 12.0133 12.0133 15.2667 8.00006 15.2667C3.9868 15.2667 0.733398 12.0133 0.733398 8.00006ZM8.00006 9.26673C7.66869 9.26673 7.40007 8.9981 7.40007 8.66673V4.66673C7.40007 4.33536 7.66869 4.06673 8.00006 4.06673C8.33144 4.06673 8.60006 4.33536 8.60006 4.66673V8.66673C8.60006 8.9981 8.33144 9.26673 8.00006 9.26673ZM8.00006 11.3334C8.36826 11.3334 8.66673 11.0349 8.66673 10.6667C8.66673 10.2985 8.36826 10.0001 8.00006 10.0001C7.63188 10.0001 7.3334 10.2985 7.3334 10.6667C7.3334 11.0349 7.63188 11.3334 8.00006 11.3334Z"
                                fill="#B62626"
                            />
                        </svg>
                    </div>
                    <div className={`${baseClass}__error--text`}>{errorMessage}</div>
                </div>
            )}
        </div>
    )
}

export default ApplyV2InputCheckbox
